import { useMemo, useState } from "react";
import { PROBLEM_CODES, api, problemCode, useApiQuery } from "../api";
import type { Grid, Int64, Expertise } from "../api";
import { Badge, Button, Card, EmptyState, Field, SectionTitle, Skeleton, Stat, Table } from "../components";
import type { TableColumn } from "../components";
import {
  compareAmounts,
  formatDays,
  formatPercent,
  formatTokens,
  fromBigInt,
  ratio,
  toBigInt,
} from "../format";
import { useCardEntrance, useGaugeFill } from "../motion";
import { useSession } from "../session";
import { AsyncBoundary, PageHeader } from "../shell";
import { EditionGrilleCarte } from "./catalogue/EditionGrilleCarte";
import styles from "./CataloguePage.module.css";

/**
 * Prestations types proposées comme aide à la lecture.
 *
 * Un TJM seul ne répond pas à la question qu'on pose devant un client
 * (« combien coûte un mois de senior ? »). Ces quatre durées y répondent sans
 * calcul mental.
 *
 * Ce sont des durées de projet, comptées en jours ouvrés : une semaine vaut
 * 5 jours-homme, un mois 20, un trimestre 60. La demi-journée a disparu avec la
 * facturation horaire — il n'existe plus de prestation qui ne soit pas un
 * nombre entier de jours.
 */
const PRESTATIONS = [
  { code: "jour", nom: "Une journée", jours: 1 },
  { code: "semaine", nom: "Une semaine", jours: 5 },
  { code: "mois", nom: "Un mois", jours: 20 },
  { code: "trimestre", nom: "Un trimestre", jours: 60 },
] as const;

type Prestation = (typeof PRESTATIONS)[number];

/** La semaine : la question la plus souvent posée en rendez-vous. */
const PRESTATION_DEFAUT: Prestation["code"] = "semaine";

function prestationParCode(code: string): Prestation {
  return PRESTATIONS.find((p) => p.code === code) ?? PRESTATIONS[1];
}

/**
 * Convertit une durée en tokens, **exactement comme le back**.
 *
 * `tokens = jours × tarif journalier`, et rien d'autre : un écran qui
 * afficherait un chiffre plus flatteur que celui que la commande va réserver
 * transformerait chaque commande en litige. La règle est celle de
 * `Expertise.TokensFor`, et depuis le passage au jour-homme elle est exacte — il
 * n'y a plus ni prorata ni arrondi à reproduire, donc plus rien qui puisse
 * diverger entre les deux implémentations.
 *
 * Le calcul se fait en `BigInt`, sur des centièmes de token : le tarif du
 * contrat est en centièmes, le coût rendu aussi, et `formatTokens` remet la
 * virgule. Convertir le tarif en `number` réintroduirait l'arrondi silencieux
 * au-delà de 2^53 que tout le back s'applique à éviter. La durée, elle, est un
 * entier de quelques dizaines de jours écrit dans ce fichier : la convertir en
 * `BigInt` est exact.
 */
function coutPrestation(tarifJournalier: Int64, jours: number): Int64 {
  return fromBigInt(toBigInt(tarifJournalier) * BigInt(jours));
}

/** Une ligne du tableau : l'expertise, son coût pour la prestation choisie, sa place. */
type Ligne = {
  expertise: Expertise;
  /** Montant en centièmes de token, chaîne décimale — jamais un `number`. */
  cout: Int64;
  /** Part du tarif le plus élevé de la grille, entre 0 et 1. */
  part: number;
};

/**
 * Barre de situation d'un tarif dans la grille.
 *
 * Composant à part parce qu'elle appelle `useGaugeFill`, et qu'un hook ne
 * s'appelle pas dans une boucle de rendu.
 */
function BarreTarif({ part, titre }: { part: number; titre: string }) {
  const remplissage = useGaugeFill<HTMLDivElement>(part);
  return (
    <div className={styles.comparaison}>
      {/* La barre redit ce que la colonne « tarif journalier » dit déjà en
          chiffres : elle n'est pas annoncée, sinon un lecteur d'écran lirait
          deux fois la même information. */}
      <div className={styles.piste} aria-hidden="true">
        <div ref={remplissage} className={styles.remplissage} />
      </div>
      <span className={styles.part} title={titre}>
        {formatPercent(part * 100)}
      </span>
    </div>
  );
}

/** La grille chargée : ses repères, son réglage de lecture et ses expertises. */
function GrilleTarifaire({ grille, onEditer }: { grille: Grid; onEditer?: () => void }) {
  const [codePrestation, setCodePrestation] = useState<string>(PRESTATION_DEFAUT);
  const prestation = prestationParCode(codePrestation);

  const reperes = useCardEntrance<HTMLDivElement>({ dependencies: [grille.version] });

  const { lignes, tarifMin, tarifMax } = useMemo(() => {
    // Tri par tarif croissant : une grille tarifaire se lit du moins cher au
    // plus cher, et l'ordre rendu par l'API n'est pas contractuel. Le libellé
    // départage deux expertises au même tarif, pour que l'ordre reste stable d'un
    // chargement à l'autre.
    const tries = [...grille.expertises].sort(
      (a, b) =>
        compareAmounts(a.tokens_per_day, b.tokens_per_day) ||
        a.label.localeCompare(b.label, "fr"),
    );

    const min = tries.length > 0 ? tries[0].tokens_per_day : "0";
    const max = tries.length > 0 ? tries[tries.length - 1].tokens_per_day : "0";

    return {
      lignes: tries.map<Ligne>((expertise) => ({
        expertise,
        cout: coutPrestation(expertise.tokens_per_day, prestation.jours),
        part: ratio(expertise.tokens_per_day, max),
      })),
      tarifMin: min,
      tarifMax: max,
    };
  }, [grille.expertises, prestation.jours]);

  const colonnes: TableColumn<Ligne>[] = [
    {
      key: "code",
      header: "Code",
      width: "9rem",
      render: (ligne) => (
        <Badge size="sm" className={styles.code}>
          {ligne.expertise.code}
        </Badge>
      ),
    },
    {
      key: "expertise",
      header: "Expertise",
      render: (ligne) => <span className={styles.libelle}>{ligne.expertise.label}</span>,
    },
    {
      key: "tarif",
      header: "Tarif journalier",
      numeric: true,
      width: "11rem",
      render: (ligne) => (
        <>
          {formatTokens(ligne.expertise.tokens_per_day)}
          <span className={styles.unite}>tokens/j</span>
        </>
      ),
    },
    {
      key: "position",
      header: "Position dans la grille",
      width: "15rem",
      render: (ligne) => (
        <BarreTarif
          part={ligne.part}
          titre={`${formatPercent(ligne.part * 100)} du tarif le plus élevé de la grille`}
        />
      ),
    },
    {
      key: "prestation",
      header: (
        <>
          {prestation.nom}
          <span className={styles.duree}>{formatDays(prestation.jours)}</span>
        </>
      ),
      numeric: true,
      width: "12rem",
      render: (ligne) => <>{formatTokens(ligne.cout)}</>,
    },
  ];

  return (
    <>
      <div className={styles.reperes} ref={reperes}>
        <Stat
          data-anim="card"
          emphasis
          label="Version en vigueur"
          value={`v${grille.version}`}
          hint={`${grille.label}. C'est cette version qui est figée sur chaque achat, et elle n'est jamais recalculée.`}
        />
        <Stat
          data-anim="card"
          label="Expertises facturables"
          value={grille.expertises.length}
          hint="Chaque expertise porte son propre tarif journalier, en tokens."
        />
        <Stat
          data-anim="card"
          label="Amplitude des tarifs"
          value={`${formatTokens(tarifMin)} – ${formatTokens(tarifMax)}`}
          unit="tokens/j"
          hint="De l'expertise la moins chère à la plus chère de la grille."
        />
      </div>

      <section className={styles.section} aria-labelledby="catalogue-expertises">
        <div className={styles.enteteSection}>
          <div className={styles.titres}>
            <SectionTitle
              id="catalogue-expertises"
              description="Les tarifs sont classés du moins cher au plus cher. La barre situe chaque expertise par rapport au tarif le plus élevé de la grille."
            >
              Expertises facturables
            </SectionTitle>
          </div>

          {onEditer !== undefined && (
            <Button variant="secondary" onClick={onEditer}>
              Modifier la grille
            </Button>
          )}

          <Field
            label="Prestation type"
            hint="Pilote la dernière colonne du tableau."
            className={styles.reglage}
          >
            {(commande) => (
              <select
                {...commande}
                value={prestation.code}
                onChange={(e) => setCodePrestation(e.target.value)}
              >
                {PRESTATIONS.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.nom} ({formatDays(p.jours)})
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>

        <Table<Ligne>
          columns={colonnes}
          rows={lignes}
          rowKey={(ligne) => ligne.expertise.id}
          caption={`Code, libellé, tarif journalier en tokens, position dans la grille et coût d'une prestation de ${formatDays(prestation.jours)}.`}
          empty={
            <EmptyState
              compact
              title="Grille publiée, mais sans expertise facturable."
              description="La version est bien en vigueur ; aucune expertise ne lui est rattachée, donc rien ne peut encore être commandé."
            />
          }
          footer={
            <div className={styles.note}>
              <span>
                Une prestation se commande en{" "}
                <strong>jours-homme entiers</strong> : son coût est une simple
                multiplication, sans prorata ni arrondi. Les montants ci-dessus sont
                donc exactement, au centième de token près, ceux qu'une commande réserverait.
              </span>
              <span className={styles.formule}>
                tokens = jours × tarif journalier
              </span>
            </div>
          }
        />
      </section>
    </>
  );
}

/**
 * Écran « catalogue » : la grille tarifaire en vigueur, telle qu'on la montre à
 * un client.
 *
 * Deux absences à ne pas confondre, et c'est la moitié du travail de cet écran :
 *
 * - **aucune grille publiée** — le serveur a répondu `404 grid_not_found`. Ce
 *   n'est pas une panne, c'est un catalogue vide : on le dit avec un
 *   `EmptyState`, pas avec une trace d'erreur ;
 * - **chargement impossible** — le serveur n'a pas répondu, ou a répondu autre
 *   chose. Là, il y a quelque chose à réessayer.
 *
 * La distinction se lit sur `problemCode(err)`, jamais sur le texte du refus :
 * `detail` est rédigé pour un humain et peut changer sans préavis.
 */
export function CataloguePage() {
  const grille = useApiQuery((o) => api.getCurrentGrid(o), []);
  const { state } = useSession();
  // Seul l'administrateur ESN publie une grille : le serveur le refuse aux
  // autres, l'écran ne leur propose pas le geste.
  const editable = state.status === "authenticated" && state.permissions.can_manage_esn;
  const [edition, setEdition] = useState(false);

  // `data === null` compte : si une grille déjà affichée disparaît pendant un
  // rechargement, on préfère garder à l'écran celle d'il y a dix secondes
  // plutôt que de vider la page sous les yeux du client.
  const aucuneGrille =
    grille.data === null && problemCode(grille.error) === PROBLEM_CODES.gridNotFound;

  return (
    <>
      <PageHeader
        eyebrow="Catalogue"
        title="Grille tarifaire"
        description="Ce que coûte chaque expertise, en tokens par jour-homme. C'est la version affichée ici qui est figée sur les achats et sur les commandes."
      />

      {aucuneGrille ? (
        <Card>
          <EmptyState
            icon="📕"
            title="Aucune grille tarifaire publiée."
            description="Aucune version n'est en vigueur : il n'y a donc aucune expertise facturable, et aucune commande n'est possible. Publiez une grille côté serveur, puis rechargez cet écran."
            action={
              <Button variant="secondary" onClick={grille.reload} loading={grille.loading}>
                Recharger
              </Button>
            }
          />
        </Card>
      ) : (
        <AsyncBoundary
          state={grille}
          errorTitle="Grille tarifaire indisponible"
          skeleton={
            <div className={styles.squelette}>
              <div className={styles.squeletteReperes}>
                <Skeleton variant="block" height="7.5rem" />
                <Skeleton variant="block" height="7.5rem" />
                <Skeleton variant="block" height="7.5rem" />
              </div>
              <Skeleton variant="block" height="22rem" />
            </div>
          }
        >
          {(donnees) =>
            edition ? (
              <EditionGrilleCarte
                grille={donnees}
                onAnnuler={() => setEdition(false)}
                onPubliee={() => {
                  setEdition(false);
                  grille.reload();
                }}
              />
            ) : (
              <GrilleTarifaire grille={donnees} onEditer={editable ? () => setEdition(true) : undefined} />
            )
          }
        </AsyncBoundary>
      )}
    </>
  );
}
